<template>
  <div class="analytics-dashboard">
    <div v-if="kitType === 2 || kitType === 3" class="dashboard-container">
      <!-- Enhanced Filter Controls -->
      <div class="filter-controls">
        <div class="date-range-picker">
          <div class="date-inputs">
            <label>
              <span class="label-text">From:</span>
              <date-picker 
                v-model="fromDate"
                type="date"
                placeholder="Start date"
                @change="handleDateChange"
              />
            </label>
            <label>
              <span class="label-text">To:</span>
              <date-picker 
                v-model="toDate"
                type="date"
                placeholder="End date"
                @change="handleDateChange"
              />
            </label>
          </div>
        </div>
        

        <!-- New Product Filter -->
        <!-- <div class="product-filter">
          <label>
            <span class="label-text">Filter by Product:</span>
            <select v-model="selectedProduct" >
              <option value="">All Products</option>
              <option v-for="product in uniqueProducts" :value="product" :key="product">
                {{ product }}
              </option>
            </select>
          </label>
        </div> -->
      </div>

      <!-- Metrics Summary Cards -->
      <div class="metrics-summary">
        <div class="metric-card" v-for="metric in summaryMetrics" :key="metric.title">
          <div class="metric-value">{{ metric.value }}</div>
          <div class="metric-title">{{ metric.title }}</div>
        </div>
      </div>

      <!-- Chart View Selector -->
      <div class="chart-selector">
        <button
          v-for="option in chartOptions"
          :key="option.value"
          @click="activeChart = option.value"
          :class="{active: activeChart === option.value}"
        >
          {{ option.label }}
        </button>
      </div>

      <!-- Main Chart -->
      <div class="chart-container">
        <div ref="mainChart" class="main-chart" style="height: 400px;"></div>
      </div>

      <!-- Secondary Charts -->
      <div class="secondary-charts">
        <div class="secondary-chart">
          <h4>Revenue by Product</h4>
          <div ref="productRevenueChart" style="height: 300px;"></div>
        </div>
        <div class="secondary-chart">
          <h4>Orders by Status</h4>
          <div ref="orderStatusChart" style="height: 300px;"></div>
        </div>
      </div>

      <!-- Data Tables -->
      <div class="data-tables">
        <div class="table-section">
          <div class="table-header" @click="toggleTable('products')">
            <h3>Revenue</h3>
            <span>{{ showProductsTable ? '▲' : '▼' }}</span>
          </div>
          <div v-show="showProductsTable" class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th @click="sortProducts('name')">Product 
                    <span v-if="productSortField === 'name'">{{ productSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th @click="sortProducts('price')">Price
                    <span v-if="productSortField === 'price'">{{ productSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th @click="sortProducts('items_sold')">Sold
                    <span v-if="productSortField === 'items_sold'">{{ productSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th @click="sortProducts('net_revenue')">Revenue
                    <span v-if="productSortField === 'net_revenue'">{{ productSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th>Order</th>
                  <th>Stock</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in sortedProducts" :key="product.id">
                  <td class="product-cell">
                    <img :src="product.image || placeholderImage" class="product-image" />
                    <span class="product-name">{{ product.name }}</span>
                  </td>
                  <td>${{ formatNumber(product.price) }}</td>
                  <td>{{ product.items_sold }}</td>
                  <td>${{ formatNumber(product.net_revenue) }}</td>
                  <td>{{ product.orders_count }}</td>
                  <td>
                    <span :class="['stock-status', product.stock_status.toLowerCase()]">
                      {{ product.stock_status }}
                    </span>
                  </td>
                  <td>
                    <button class="action-btn view-btn" @click="viewProduct(product)">View</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="products.length === 0" class="no-data">No products found</div>
          </div>
        </div>

        <div class="table-section">
          <div class="table-header" @click="toggleTable('orders')">
            <h3>Orders</h3>
            <span>{{ showOrdersTable ? '▲' : '▼' }}</span>
          </div>
          <div v-show="showOrdersTable" class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th @click="sortOrders('date')">Date
                    <span v-if="orderSortField === 'date'">{{ orderSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th>Order #</th>
                  <th @click="sortOrders('status')">Status
                    <span v-if="orderSortField === 'status'">{{ orderSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th>Customer</th>
                  <th>Products</th>
                  <th @click="sortOrders('items_sold')">Items
                    <span v-if="orderSortField === 'items_sold'">{{ orderSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th @click="sortOrders('net_sales')">Total
                    <span v-if="orderSortField === 'net_sales'">{{ orderSortDirection === 'asc' ? '↑' : '↓' }}</span>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="order in paginatedOrders" :key="order.order_id">
                  <td>{{ formatDate(order.date) }}</td>
                  <td>#{{ order.order_id }}</td>
                  <td>
                    <span :class="['order-status', order.status.toLowerCase()]">
                      {{ formatStatus(order.status) }}
                    </span>
                  </td>
                  <td>{{ order.customer }}</td>
                  <td>{{ order.products }}</td>
                  <td>{{ order.items_sold }}</td>
                  <td>${{ formatNumber(order.net_sales) }}</td>
                  <td>
                    <button class="action-btn view-btn" @click="viewOrder(order)">View</button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="filteredOrders.length === 0" class="no-data">No orders found</div>

            <!-- Pagination -->
            <div class="pagination-container" v-if="count_page > 1">
              <div class="pagination-info">
                Showing {{ paginationStart }} - {{ paginationEnd }} of {{ filteredOrders.length }} orders
              </div>
              
              <Paginate
                v-if="count_page > 1"
                :page-count="count_page"
                :page-class="'pogination-item'"
                :click-handler="changePage"
                :prev-text="'<'"
                :next-text="'>'"
                :container-class="'pogination-wrap'"
                :force-page="currentPage"
              ></Paginate>
              
              <div class="items-per-page">
                <label>Items per page:</label>
                <select v-model="itemsPerPage" @change="changeItemsPerPage">
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading State -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading data...</div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import * as echarts from "echarts";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/en';
import { mapState, mapGetters, mapActions } from 'vuex';
import Paginate from 'vuejs-paginate';

export default {
  name: "EnhancedAnalyticsDashboard",
  components: {
    DatePicker,
    Paginate
  },
  data() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    
    return {
      kitType: 3,
      fromDate: startDate,
      toDate: endDate,
      timeGrouping: 'day',
      activeChart: 'revenue',
      currentPage: 1,
      itemsPerPage: 10,
      // Data
      ordersData: [],
      summaryMetrics: [
        { title: 'Total Revenue', value: '$0', change: null },
        { title: 'Total Orders', value: '0', change: null },
        { title: 'Top Product', value: 'N/A', change: null }
      ],
      
      // UI State
      showProductsTable: true,
      showOrdersTable: true,
      productSortField: 'net_revenue',
      productSortDirection: 'desc',
      orderSortField: 'date',
      orderSortDirection: 'desc',
      
      // Chart Options
      chartOptions: [
        { value: 'revenue', label: 'Revenue' },
        { value: 'orders', label: 'Orders' }
      ],
      
      // Placeholder
      placeholderImage: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0xOCA2SDFtMTcgMTJIMW0xNC05SDVtMTQgNEg1bTE0IDRINW0xNCA0SDVtMTQgNEg1Ii8+PC9zdmc+'
    };
  },
   computed: {
    ...mapState('wooAnalytics', ['loading']),
    ...mapGetters('wooAnalytics', [
      'getRevenueData',
      'getOrders',
      'getAllOrders', 
      'getProducts',
      'getSelectedProduct'
    ]),
    
    // Remove the old computed properties that were causing recursion
    revenueData() {
      return this.getRevenueData;
    },
    
    orders() {
      return this.getOrders; // This now gets filtered orders from the store
    },
    
    allOrders() {
      return this.getAllOrders;
    },
    
    products() {
      return this.getProducts;
    },

    // Update this to use the store's selected product
    selectedProduct: {
      get() {
        return this.getSelectedProduct;
      },
      set(value) {
        this.filterOrdersByProduct(value);
        this.updateSummaryMetrics();
      }
    },

    // Remove the old filteredOrders computed property since getOrders now handles filtering
    // Replace it with a simple reference to the store's filtered orders
    filteredOrders() {
      return this.getOrders; // Already filtered by the store getter
    },

    sortedProducts() {
      return [...this.products].sort((a, b) => {
        const modifier = this.productSortDirection === 'asc' ? 1 : -1;
        if (a[this.productSortField] < b[this.productSortField]) return -1 * modifier;
        if (a[this.productSortField] > b[this.productSortField]) return 1 * modifier;
        return 0;
      });
    },
    
    // Update sortedOrders to work with the filtered orders
    sortedOrders() {
      return [...this.filteredOrders].sort((a, b) => {
        const modifier = this.orderSortDirection === 'asc' ? 1 : -1;
        
        if (this.orderSortField === 'date') {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return (dateA - dateB) * modifier;
        }
        
        if (a[this.orderSortField] < b[this.orderSortField]) return -1 * modifier;
        if (a[this.orderSortField] > b[this.orderSortField]) return 1 * modifier;
        return 0;
      });
    },
    groupedOrdersByDate() {
      const grouped = {};
      
      this.orders.forEach(order => {
        const date = order.date; // Assuming order.date is in 'YYYY-MM-DD' format
        if (!grouped[date]) {
          grouped[date] = {
            date: date,
            count: 0,
            items_sold: 0,
            net_sales: 0
          };
        }
        grouped[date].count += 1;
        grouped[date].items_sold += parseInt(order.items_sold) || 0;
        grouped[date].net_sales += parseFloat(order.net_sales) || 0;
      });
      
        return Object.values(grouped).sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });

    },
    
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.sortedOrders.slice(start, start + this.itemsPerPage);
    },
    
    count_page() {
      return Math.ceil(this.sortedOrders.length / this.itemsPerPage);
    },
    
    paginationStart() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    paginationEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.sortedOrders.length);
    },
    
    // Update uniqueProducts to use allOrders
    uniqueProducts() {
      const products = new Set();
      this.allOrders.forEach(order => {
        if (order.products && order.products !== 'No Products') {
          // Handle string format
          if (typeof order.products === 'string') {
            order.products.split(', ').forEach(product => {
              products.add(product.trim());
            });
          }
          // Handle array format
          else if (Array.isArray(order.products)) {
            order.products.forEach(product => {
              if (product.name) {
                products.add(product.name.trim());
              }
            });
          }
        }
      });
      return Array.from(products).sort();
    }
  },
  methods: {
    ...mapActions('wooAnalytics', [
      'fetchAnalyticsData',
      'filterOrdersByProduct'
    ]),
    async fetchData() {
       if (!this.fromDate || !this.toDate) return;
      
      const success = await this.fetchAnalyticsData({
        startDate: this.fromDate,
        endDate: this.toDate
      });
      
      if (success) {
        this.updateSummaryMetrics();
        this.$nextTick(() => {
          this.renderCharts();
        });
      }
    },
    
    
    updateSummaryMetrics() {
      // Calculate total revenue
      const totalRevenue = this.revenueData.reduce((sum, item) => sum + parseFloat(item.total || 0), 0);
      this.summaryMetrics[0].value = `$${this.formatNumber(totalRevenue)}`;
      
      // Calculate total orders
      this.summaryMetrics[1].value = this.orders.length.toString();
      
      // Find top product
      if (this.products.length > 0) {
        const topProduct = [...this.products].sort((a, b) => b.net_revenue - a.net_revenue)[0];
        this.summaryMetrics[2].value = topProduct.name;
      }
    },
    
    renderCharts() {
      this.renderMainChart();
      this.renderProductRevenueChart();
      this.renderOrderStatusChart();
    },
    
    renderMainChart() {
      if (!this.$refs.mainChart) return;
      
      const chart = echarts.getInstanceByDom(this.$refs.mainChart) || 
                    echarts.init(this.$refs.mainChart);
      
      let xAxisData, seriesData, yAxisName, seriesName;
      
      switch (this.activeChart) {
        case 'revenue':
          xAxisData = this.revenueData.map(item => item.date);
          seriesData = this.revenueData.map(item => parseFloat(item.total || 0));
          yAxisName = 'Revenue ($)';
          seriesName = 'Revenue';
          break;
          
        case 'orders':
          xAxisData = this.groupedOrdersByDate.map(order => order.date);
          seriesData = this.groupedOrdersByDate.map(order => parseFloat(order.items_sold || 0));
          yAxisName = 'Items Sold';
          seriesName = 'Orders';
          break;
          
        
      }
      
      const option = {
        title: {
          text: `${seriesName} Over Time`,
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 'normal'
          }
        },
        tooltip: {
          trigger: 'axis',
          formatter: params => {
            const date = params[0].axisValue;
            const value = params[0].data;
            return `
              <div style="font-weight:bold;margin-bottom:5px">${date}</div>
              <div>${seriesName}: ${this.activeChart === 'revenue' || this.activeChart === 'aov' ? '$' : ''}${this.formatNumber(value)}</div>
            `;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLabel: {
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          name: yAxisName,
          nameLocation: 'middle',
          nameGap: 40
        },
        series: [{
          name: seriesName,
          type: 'line',
          smooth: true,
          data: seriesData,
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(58, 77, 233, 0.8)' },
              { offset: 1, color: 'rgba(58, 77, 233, 0.1)' }
            ])
          },
          itemStyle: {
            color: '#005eb8'
          },
          lineStyle: {
            width: 3
          }
        }]
      };
      
      chart.setOption(option);
      window.addEventListener('resize', chart.resize);
    },
    
    renderProductRevenueChart() {
      if (!this.$refs.productRevenueChart) return;
      
      const chart = echarts.getInstanceByDom(this.$refs.productRevenueChart) || 
                    echarts.init(this.$refs.productRevenueChart);
      
      // Get top 5 products by revenue
      const topProducts = [...this.products]
        .sort((a, b) => b.net_revenue - a.net_revenue)
        .slice(0, 5);
        // Extract legend names
      const legendData = topProducts.map(item => item.name);
      const option = {
        legend: {
          orient: 'horizontal',
          bottom: '5%',
          left: 'center',
          data: legendData, // Use the extracted legend data
          itemGap: 15,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: {
            color: '#333',
            fontSize: 11,
            width: 80, // Limit text width
            overflow: 'truncate'
          },
          formatter: function(name) {
            // Truncate long names for horizontal layout
            if (name.length > 12) {
              return name.substring(0, 10) + '...';
            }
            return name;
          },
          // Handle wrapping for many items
          type: 'scroll',
          pageButtonItemGap: 10,
          pageIconColor: '#005eb8',
          pageIconInactiveColor: '#aaa',
          pageTextStyle: {
            color: '#333',
            fontSize: 10
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: params => {
            return `
              <div style="font-weight:bold;margin-bottom:5px">${params.name}</div>
              <div>Revenue: $${this.formatNumber(params.value)}</div>
              <div>Percentage: ${params.percent}%</div>
            `;
          }
        },
        series: [{
          name: 'Product Revenue',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '18',
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: topProducts.map(product => ({
            value: product.net_revenue,
            name: product.name
          }))
        }]
      };
      
      chart.setOption(option);
      window.addEventListener('resize', chart.resize);
    },
    
    renderOrderStatusChart() {
      if (!this.$refs.orderStatusChart) return;
      
      const chart = echarts.getInstanceByDom(this.$refs.orderStatusChart) || 
                    echarts.init(this.$refs.orderStatusChart);
      
      // Count orders by status
      const statusCounts = {};
      this.orders.forEach(order => {
        statusCounts[order.status] = (statusCounts[order.status] || 0) + 1;
      });
      
      const statusColors = {
        'processing': '#ffb822',
        'completed': '#1dc9b7',
        'on-hold': '#fd397a',
        'cancelled': '#fd397a',
        'refunded': '#5d78ff',
        'failed': '#ff5c75',
        'delivered': '#6f42c1' // New delivered status color
      };
      
      const data = Object.entries(statusCounts).map(([status, count]) => ({
        name: this.formatStatus(status),
        value: count,
        itemStyle: { color: statusColors[status.toLowerCase()] || '#5d78ff' }
      }));
      const legendData = Object.entries(statusCounts).map(([status, count]) => ({
        name: this.formatStatus(status)
      }));
      const option = {
        legend: {
          orient: 'horizontal',
          bottom: '5%',
          left: 'center',
          data: legendData, // Use the extracted legend data
          itemGap: 15,
          itemWidth: 12,
          itemHeight: 12,
          textStyle: {
            color: '#333',
            fontSize: 11,
            width: 80, // Limit text width
            overflow: 'truncate'
          },
          formatter: function(name) {
            // Truncate long names for horizontal layout
            if (name.length > 12) {
              return name.substring(0, 10);
            }
            return name;
          },
          // Handle wrapping for many items
          type: 'scroll',
          pageButtonItemGap: 10,
          pageIconColor: '#005eb8',
          pageIconInactiveColor: '#aaa',
          pageTextStyle: {
            color: '#333',
            fontSize: 10
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: params => {
            return `
              <div style="font-weight:bold;margin-bottom:5px">${params.name}</div>
              <div>Orders: ${params.value}</div>
              <div>Percentage: ${params.percent}%</div>
            `;
          }
        },
        series: [{
          name: 'Order Status',
          type: 'pie',
          radius: '70%',
          center: ['50%', '50%'],
          data: data,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            formatter: '{b}: {c} ({d}%)'
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      };
      
      chart.setOption(option);
      window.addEventListener('resize', chart.resize);
    },
    
    handleDateChange() {
      this.filterOrdersByProduct('');
      this.currentPage = 1;
      this.fetchData();
    },
     sortOrders(field) {
      if (this.orderSortField === field) {
        this.orderSortDirection = this.orderSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.orderSortField = field;
        this.orderSortDirection = 'desc';
      }
      this.currentPage = 1;
    },
    
    changePage(page) {
      this.currentPage = page;
    },
    
    changeItemsPerPage() {
      this.currentPage = 1;
      this.itemsPerPage = Number(this.itemsPerPage);
    },
    
    toggleTable(table) {
      if (table === 'products') {
        this.showProductsTable = !this.showProductsTable;
      } else if (table === 'orders') {
        this.showOrdersTable = !this.showOrdersTable;
      }
    },
    
    sortProducts(field) {
      if (this.productSortField === field) {
        this.productSortDirection = this.productSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.productSortField = field;
        this.productSortDirection = 'desc';
      }
    },
    
    sortOrders(field) {
      if (this.orderSortField === field) {
        this.orderSortDirection = this.orderSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.orderSortField = field;
        this.orderSortDirection = 'desc';
      }
    },
    
    viewProduct(product) {
      window.open(product.permalink, '_blank');
    },
    
    viewOrder(order) {
      window.open(order.permalink, '_blank');
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    formatDateForAPI(date) {
      return date.toISOString().split('T')[0];
    },
    
    formatNumber(value) {
      return parseFloat(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    
    formatStatus(status) {
      const statusMap = {
        'processing': 'Processing',
        'processing-west': 'Processing (West)',
        'completed': 'Completed',
        'on-hold': 'On Hold',
        'cancelled': 'Cancelled',
        'refunded': 'Refunded',
        'failed': 'Failed',
        'delivered': 'Delivered' // New delivered status
      };
      return statusMap[status.toLowerCase()] || status;
    }
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    activeChart() {
      this.renderMainChart();
    },
  }
};
</script>

<style scoped lang="scss">
/* Your existing styles plus the pagination styles I provided earlier */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 10px 0;
  border-top: 1px solid #eee;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 10px;
}

.items-per-page select {
  padding: 5px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.pogination-wrap {
  display: flex;
  gap: 5px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.pogination-item {
  padding: 5px 10px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  border-radius: 4px;
}

.pogination-item.active {
  background: #005eb8;
  color: white;
  border-color: #005eb8;
}

.pogination-item:hover:not(.active) {
  background: #f5f5f5;
}
.analytics-dashboard {
  position: relative;
  font-family: 'TTNormsRegular', sans-serif;
  color: #333;
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.dashboard-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.filter-controls {
  width:50%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
  
  .date-range-picker {
    flex: 1;
    min-width: 300px;
    
    .date-inputs {
      display: flex;
      gap: 15px;
      flex-wrap: wrap;
      
      label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        min-width: 140px;
        flex: 1;
      }
    }
  }
  
  .view-options,
  .product-filter {
    flex: 1;
    min-width: 200px;
  }
  
  label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
  }
  
  .label-text {
    font-weight: 500;
    color: #555;
    white-space: nowrap;
  }
  
  select, .mx-input {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #fff;
    font-size: 14px;
    flex-grow: 1;
    min-width: 120px;
    
    &:focus {
      outline: none;
      border-color: #005eb8;
      box-shadow: 0 0 0 2px rgba(58, 77, 233, 0.2);
    }
  }
}

.metrics-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
  
  .metric-card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid #eee;
    text-align: center;
    
    .metric-value {
      font-size: 24px;
      font-weight: 600;
      color: #005eb8;
      margin-bottom: 5px;
    }
    
    .metric-title {
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    }
    
    .metric-change {
      font-size: 12px;
      font-weight: 500;
      
      &.positive {
        color: #1dc9b7;
      }
      
      &.negative {
        color: #fd397a;
      }
    }
  }
}

.chart-selector {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  
  button {
    padding: 8px 16px;
    background: #f5f6fa;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: #e9ebf5;
    }
    
    &.active {
      background: #005eb8;
      color: white;
    }
  }
}

.chart-container {
  background: #fff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 25px;
  border: 1px solid #eee;
  
  .main-chart {
    width: 100%;
  }
}

.secondary-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  
  .secondary-chart {
    background: #fff;
    border-radius: 8px;
    padding: 15px;
    border: 1px solid #eee;
    
    h4 {
      margin: 0 0 15px 0;
      font-size: 16px;
      text-align: center;
      color: #555;
    }
  }
}

.data-tables {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  
  .table-header {
    padding: 15px 20px;
    background: #f9fafc;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    h3 {
      margin: 0;
      font-size: 16px;
      color: #005eb8;
    }
    
    span {
      color: #999;
      font-size: 12px;
    }
    
    &:hover {
      background: #f5f7fb;
    }
  }
  
  .table-wrapper {
    overflow-x: auto;
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th {
    background: #f9fafc;
    padding: 12px 15px;
    text-align: left;
    font-weight: 500;
    color: #555;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    white-space: nowrap;
    
    &:hover {
      background: #f5f7fb;
    }
    
    span {
      margin-left: 5px;
      font-size: 12px;
    }
  }
  
  td {
    padding: 12px 15px;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover td {
    background: #f9fafc;
  }
  
  .product-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .product-image {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #eee;
  }
  
  .product-name {
    flex: 1;
    min-width: 150px;
  }
  
  .stock-status {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    
    &.instock {
      background: #e6f7f5;
      color: #1dc9b7;
    }
    
    &.outofstock {
      background: #ffebee;
      color: #fd397a;
    }
    
    &.onbackorder {
      background: #fff8e1;
      color: #ffb822;
    }
  }
  
  .order-status {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    
    &.processing, &.processing-west {
      background: #fff8e1;
      color: #ffb822;
    }
    
    &.completed {
      background: #e8f5e9;  
      color: #2e7d32;  
    }
    
    &.on-hold {
      background: #e1e9ff;
      color: #5d78ff;
    }
    
    &.cancelled, &.failed {
      background: #ffebee;
      color: #fd397a;
    }
    
    &.refunded {
      background: #f0f0f0;
      color: #666;
    }

    &.delivered {
      background: #e6f7f5;
      color: #1dc9b7;
    }
  }
  
  .action-btn {
    padding: 5px 10px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    
    &.view-btn {
      background: #e1e9ff;
      color: #005eb8;
      
      &:hover {
        background: #d0dbfa;
      }
    }
  }
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-style: italic;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #005eb8;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
  }
  
  .loading-text {
    color: #555;
    font-size: 14px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Date picker overrides */
.mx-datepicker {
  width: 100%;
}

.mx-input {
  height: auto;
  box-shadow: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .filter-controls {
    flex-direction: column;
    align-items: stretch;
    
    .date-range-picker {
      min-width: 100%;
      
      .date-inputs {
        flex-direction: column;
        gap: 10px;
        
        label {
          min-width: 100%;
        }
      }
    }
    
    .view-options,
    .product-filter {
      min-width: 100%;
    }
  }
  
  .metrics-summary {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .metrics-summary {
    grid-template-columns: 1fr;
  }
  
  .data-table {
    font-size: 13px;
    
    th, td {
      padding: 8px 10px;
    }
  }
  
  .date-inputs {
    flex-direction: column;
    
    label {
      min-width: 100%;
    }
  }
}
</style>